/**
 * 冷啟動狀態文案的**純推導層**(給 `BootSplash`、首頁同步列、登入頁佔位共用)
 * -----------------------------------------------------------------------------
 * 為什麼要有這一層:訊息的選擇邏輯是「階段 × 已等待多久」的純函式,抽出來才測得到
 * ——元件那側要跑真計時器,用 fake timer 驗訊息切換會跟 i18n 的 async setup 打架
 * (`ReportsDatePicker.test` 檔頭記過這個坑)。所以**時間邏輯在這裡測、元件只測接線**。
 *
 * ★ **R314 / R317 紅線:每一則文案在顯示的當下都必須是真的**。這不是裝飾性的
 * 「載入中動畫」,是在對使用者陳述系統狀態:
 *
 *   - `phase` 來自**真正卡住的那一關**(auth bootstrap / workspace bootstrap /
 *     首頁資料 / 等 GIS 畫按鈕 / 換 token),不是輪播 —— 講「正在載入工作區」的時候
 *     就真的在打 `GET /me` + `/groups`。
 *   - **輪播的是 hint 不是 phase**(R317):owner 實測冷啟動平均 20–30 秒,兩句話撐不住
 *     那麼久(「感覺沒有在動」)。所以 hint 改成 {@link BOOT_HINT_COUNT} 句輪替 ——
 *     但**每一句都是對「現在這個等待」成立的陳述**,不是假造的進度階段。
 *     ⚠️ **絕對不可以**寫成「正在載入模組⋯⋯正在連線資料庫⋯⋯」這種假階段:那是編造,
 *     而且第一個看 network tab 的人就會發現。
 *   - ⚠️ **不可以加進度百分比或預估剩餘秒數** —— 我們沒有任何可據以計算的東西,編一個
 *     出來就是 R292.17「畫 0 會被讀成檢查過而且沒問題」的同族:**假的確定性比誠實的
 *     等待更糟**。(「首次啟動可能需要 20–30 秒」不同 —— 那是 owner 的實測範圍,而且
 *     講的是一般預期不是這一次的倒數。)
 */

/**
 * 目前真正卡住的那一關。
 *
 * `auth` / `workspace` = RequireAuth 兩道 gate;`sync` = 首頁資料載入列;
 * `google` = 登入頁等後端給 client_id + GIS 把按鈕畫出來;`signin` = 換 token 中。
 */
export type BootPhase = 'auth' | 'workspace' | 'sync' | 'google' | 'signin';

/** 超過這個秒數才開始補說明(短載入不打擾)。 */
export const BOOT_HINT_AFTER_MS = 3_000;

/** 每隔這麼久換一句(輪播節奏 —— 太快像跑馬燈,太慢又回到「沒在動」)。 */
export const BOOT_HINT_ROTATE_MS = 3_000;

/** `boot.hints` 陣列的長度。⚠️ 改這裡要同步改三語 JSON,`bootStatus.test` 會擋。 */
export const BOOT_HINT_COUNT = 10;

/** i18n key(literal union;strict-typed `t()` 不吃 template literal)。 */
export type BootTitleKey =
  | 'boot.auth'
  | 'boot.workspace'
  | 'boot.sync'
  | 'boot.google'
  | 'boot.signingIn';

export interface BootStatus {
  titleKey: BootTitleKey;
  /**
   * `boot.hints` 陣列的索引;`null` = 還沒久到需要解釋(前幾秒只顯階段句)。
   *
   * 回索引而不是 key 字串,是為了讓三語 JSON 用**陣列**存這 10 句 —— 對外部翻譯
   * 人員來說,一個陣列比 `hint1`…`hint10` 十個 key 好讀得多。
   */
  hintIndex: number | null;
}

const TITLE_BY_PHASE: Record<BootPhase, BootTitleKey> = {
  auth: 'boot.auth',
  workspace: 'boot.workspace',
  sync: 'boot.sync',
  google: 'boot.google',
  signin: 'boot.signingIn',
};

/**
 * 階段 + 已等待毫秒 → 要顯示的階段句 + hint 索引。
 *
 * @param phase 目前真正卡住的那一關(呼叫端從真實 gate 推導,不可自己輪播)
 * @param elapsedMs 從畫面掛上算起的毫秒數(單調遞增)
 */
export function bootStatusKeys(phase: BootPhase, elapsedMs: number): BootStatus {
  const titleKey = TITLE_BY_PHASE[phase];
  if (elapsedMs < BOOT_HINT_AFTER_MS) return { titleKey, hintIndex: null };
  const ticks = Math.floor((elapsedMs - BOOT_HINT_AFTER_MS) / BOOT_HINT_ROTATE_MS);
  return { titleKey, hintIndex: ticks % BOOT_HINT_COUNT };
}
